/**
 * AdminDeepAnalysisPage — Phase 8
 *
 * Shows every Deep Analysis ever generated:
 *   Symbol · Date · Signal · Conviction · Views · Cost · Valid until · Actions
 *
 * Filters:
 *   All | Valid | Expired | This month
 *
 * Per-row actions:
 *   View   → /stocks/{company_code} (Deep Analysis is on Overview tab)
 *   Expire → admin POST that flips is_outdated=true (mark only)
 *   Re-run → admin POST that triggers a fresh analysis run
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  RefreshCcw,
  Eye,
  Loader2,
  AlertTriangle,
  Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import LoadingState from "@/components/common/LoadingState";
import SignalBadge from "@/components/common/SignalBadge";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "valid", label: "Valid" },
  { key: "expired", label: "Expired" },
  { key: "month", label: "This month" },
];

const fmtUsd = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;

const AdminDeepAnalysisPage = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState({}); // {id: 'expire'|'rerun'}
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        apiGet("/admin/deep-analysis/list?limit=500"),
        apiGet("/admin/deep-analysis/stats"),
      ]);
      setItems(list?.items || []);
      setStats(s);
    } catch (e) {
      toast.error(`Failed to load: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleExpire = useCallback(
    async (id, symbol) => {
      setBusy((b) => ({ ...b, [id]: "expire" }));
      try {
        await apiPost(`/admin/deep-analysis/${id}/expire`);
        toast.success(`Marked ${symbol} as outdated`);
        await load();
      } catch (e) {
        toast.error(`Failed to expire: ${e?.message || e}`);
      } finally {
        setBusy((b) => {
          const next = { ...b };
          delete next[id];
          return next;
        });
      }
    },
    [load]
  );

  const handleRerun = useCallback(
    async (symbol) => {
      setBusy((b) => ({ ...b, [symbol]: "rerun" }));
      toast.info(`Running fresh analysis for ${symbol} — this takes ~2 minutes`);
      try {
        const r = await apiPost(`/stocks/${encodeURIComponent(symbol)}/deep-analysis`);
        toast.success(
          r?.reused ? `${symbol}: existing analysis still valid (no quota used)` : `${symbol}: new analysis ready`
        );
        await load();
      } catch (e) {
        toast.error(`Re-run failed: ${e?.response?.data?.detail || e?.message || e}`);
      } finally {
        setBusy((b) => {
          const next = { ...b };
          delete next[symbol];
          return next;
        });
      }
    },
    [load]
  );

  const refreshCache = useCallback(async (kind) => {
    setRefreshing(true);
    try {
      const url =
        kind === "macro"
          ? "/admin/deep-analysis/refresh-macro-cache"
          : "/admin/deep-analysis/refresh-geo-cache";
      const r = await apiPost(url);
      toast.success(`${kind} cache refreshed · ${r?.sectors_scored ?? 0} sectors`);
      await load();
    } catch (e) {
      toast.error(`Refresh failed: ${e?.message || e}`);
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const now = Date.now();
    const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
    return items.filter((it) => {
      if (filter === "all") return true;
      const validUntil = it.valid_until ? new Date(it.valid_until).getTime() : 0;
      const generated = it.generated_at ? new Date(it.generated_at).getTime() : 0;
      if (filter === "valid") return validUntil > now && !it.is_outdated;
      if (filter === "expired") return validUntil <= now || it.is_outdated;
      if (filter === "month") return generated >= monthStart;
      return true;
    });
  }, [items, filter]);

  if (loading) return <LoadingState message="Loading deep analyses…" />;

  return (
    <div className="space-y-6 p-6" data-testid="admin-deep-analysis-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Deep Analysis Ledger
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every 6-specialist research report run on the platform · 30-day shared cache
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => refreshCache("macro")}
            disabled={refreshing}
            data-testid="admin-da-refresh-macro"
          >
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
            Refresh macro cache
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refreshCache("geo")}
            disabled={refreshing}
            data-testid="admin-da-refresh-geo"
          >
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
            Refresh geo cache
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Total analyses
              </div>
              <div className="font-display text-2xl tabular-nums" data-testid="admin-da-total">
                {stats.total_analyses ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Currently valid
              </div>
              <div className="font-display text-2xl tabular-nums" data-testid="admin-da-valid">
                {stats.valid_analyses ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                This month
              </div>
              <div className="font-display text-2xl tabular-nums" data-testid="admin-da-month">
                {stats.analyses_this_month ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Cost this month
              </div>
              <div className="font-display text-2xl tabular-nums" data-testid="admin-da-cost">
                {fmtUsd(stats.cost_this_month_usd)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter bar */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 pt-0">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              onClick={() => setFilter(f.key)}
              data-testid={`admin-da-filter-${f.key}`}
            >
              {f.label}
              <Badge variant="secondary" className="ml-2 text-[10px] tabular-nums">
                {f.key === "all"
                  ? items.length
                  : items.filter((it) => {
                      const now = Date.now();
                      const monthStart = new Date(
                        new Date().getFullYear(),
                        new Date().getMonth(),
                        1
                      ).getTime();
                      const validUntil = it.valid_until ? new Date(it.valid_until).getTime() : 0;
                      const generated = it.generated_at ? new Date(it.generated_at).getTime() : 0;
                      if (f.key === "valid") return validUntil > now && !it.is_outdated;
                      if (f.key === "expired") return validUntil <= now || it.is_outdated;
                      if (f.key === "month") return generated >= monthStart;
                      return false;
                    }).length}
              </Badge>
            </Button>
          ))}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No analyses match this filter.
            </div>
          ) : (
            <Table data-testid="admin-da-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Symbol</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Signal</TableHead>
                  <TableHead>Conviction</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Valid until</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => {
                  const signal = (it.signal || "").toUpperCase();
                  const valid = it.valid_until ? new Date(it.valid_until).getTime() > Date.now() : false;
                  const isExpired = !valid || it.is_outdated;
                  return (
                    <TableRow key={it._id} data-testid={`admin-da-row-${it.symbol}`}>
                      <TableCell className="font-mono">{it.symbol}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {it.generated_at ? formatDateTime(it.generated_at) : "—"}
                      </TableCell>
                      <TableCell>
                        {signal && (
                          <SignalBadge signal={signal} size="sm" />
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{it.conviction || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{it.view_count ?? 0}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                        {fmtUsd(it.total_cost_usd)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <span
                          className={
                            isExpired
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-muted-foreground"
                          }
                        >
                          {it.valid_until ? formatDateTime(it.valid_until).split(",")[0] : "—"}
                          {isExpired && (
                            <Badge
                              variant="outline"
                              className="ml-2 border-amber-500/40 text-amber-700 dark:text-amber-400 text-[10px]"
                            >
                              {it.is_outdated ? "outdated" : "expired"}
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link
                            to={`/stocks/${it.company_code || it.symbol.split(".")[0]}`}
                            data-testid={`admin-da-view-${it.symbol}`}
                          >
                            <Button size="sm" variant="outline">
                              <Eye className="h-3.5 w-3.5 mr-1" /> View
                            </Button>
                          </Link>
                          {!isExpired && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleExpire(it._id, it.symbol)}
                              disabled={busy[it._id] === "expire"}
                              data-testid={`admin-da-expire-${it.symbol}`}
                            >
                              {busy[it._id] === "expire" ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                              )}
                              Expire
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleRerun(it.symbol)}
                            disabled={busy[it.symbol] === "rerun"}
                            data-testid={`admin-da-rerun-${it.symbol}`}
                          >
                            {busy[it.symbol] === "rerun" ? (
                              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                              <RefreshCcw className="h-3.5 w-3.5 mr-1" />
                            )}
                            Re-run
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDeepAnalysisPage;
